/**
 * The **incremental client replica** (PRD #624 / issue #627, ADR-0324): the
 * client-owned read-model {@link World} that ordinary field-level
 * {@link WorldDeltaMessage}s mutate **in place**, instead of reconstructing a
 * complete world from a full snapshot on every broadcast.
 *
 * Before this, every 10Hz delta was applied onto a held baseline snapshot
 * (`applyDelta`, an O(entities) Record copy) and then the *whole* world was
 * rebuilt through `snapshotToWorld` (every entity's every component re-cloned,
 * every `Mobile` re-instantiated) - even when a single rat scurried one tile.
 * The replica applies only what the delta names:
 *
 * - **added** entities are rebuilt once, from their full snapshot;
 * - **removed** ids are deleted;
 * - **changed** entities have only their changed components rebuilt (a fresh
 *   object per changed component) and their `cleared` components deleted;
 * - **everything else keeps object identity** - an unchanged entity and its
 *   unchanged components are the very same objects the previous tick held, so
 *   per-update allocation is proportional to what changed, not to world size.
 *
 * Component application is **descriptor-driven** (the ADR-0260 table): each
 * component value crosses from wire snapshot to live form through its
 * {@link COMPONENT_DESCRIPTORS} `rebuild`, so a newly declared component flows
 * through with **no client branch** - and a key with no descriptor is dropped,
 * exactly as the persistence `rebuildEntity` (which walks the same table) drops
 * it on the full-reconstruction path. The table is injectable for tests only.
 *
 * The **authoritative recovery path is preserved**: a full map-bearing
 * `Snapshot` (connect/reconnect) or a periodic map-less `Keyframe` replaces the
 * replica **wholesale** through the unchanged `snapshotToWorld` persistence
 * path, discarding any stale incremental state. Deltas cannot outlive a bad
 * frame either: if an apply throws part-way (a malformed component payload),
 * the replica discards its world so no *further* deltas compound the damage.
 * Note the presentation shell keeps rendering its last-emitted world - which in
 * that case is the partially-mutated one - so a **stale-partial frame may be
 * shown for at most one keyframe interval** (the server keyframes ~1/sec,
 * ADR-0163) before the wholesale resync replaces it. The old model showed the
 * last fully-consistent world for that same window instead; both self-heal at
 * the keyframe.
 *
 * Presentation-only, by construction: nothing here originates state - every
 * mutation is a verbatim adoption of what the server sent, and the wire format
 * (`diffSnapshot`/the binary codec, ADR-0164/0190) is untouched. Gameplay
 * authority stays on the server.
 */

import type { World } from '../sim/index';
import type { Entity, EntityId } from '../sim/entity';
import type { MapData } from '../sim/map';

import type { EntitySnapshot } from '../sim/persistence';
import { COMPONENT_DESCRIPTORS } from '../sim/componentDescriptors';
import {
  snapshotToWorld,
  type EntityFieldDelta,
  type WorldDeltaMessage,
  type WorldKeyframeMessage,
  type WorldSnapshotMessage,
} from './protocol';

/**
 * The shape the replica needs of one descriptor: how to turn a wire component
 * snapshot into its live form. Structurally satisfied by every entry of the
 * shipped {@link COMPONENT_DESCRIPTORS} table; widened to `unknown` so a test
 * can inject a fixture descriptor for a not-yet-declared component.
 */
export interface ReplicaComponentDescriptor {
  rebuild: (snap: unknown) => unknown;
}

/** A descriptor lookup keyed by component name; absent = unknown component. */
export type ReplicaDescriptorTable = Readonly<
  Record<string, ReplicaComponentDescriptor | undefined>
>;

/** The shipped table, widened to the replica's structural view of it. */
const DEFAULT_DESCRIPTORS =
  COMPONENT_DESCRIPTORS as unknown as ReplicaDescriptorTable;

export class ClientReplica {
  /** The live read-model world, mutated in place by {@link applyDelta}. */
  private world?: World;
  /** The immutable map + schema version retained for map-less keyframe resyncs. */
  private map?: MapData;
  private version?: number;

  constructor(
    private readonly descriptors: ReplicaDescriptorTable = DEFAULT_DESCRIPTORS,
  ) {}

  /** The current replica world, or `undefined` before any baseline (or after a bad frame). */
  get current(): World | undefined {
    return this.world;
  }

  /**
   * Adopt a full, map-bearing snapshot (a Welcome/connect/reconnect seed or a
   * mid-stream authoritative `Snapshot`): **construct the replica wholesale**
   * through the unchanged persistence path, discarding any incremental state.
   * Throws on a schema-version skew, exactly as before (the caller routes that
   * to the reload flow).
   */
  adoptSnapshot(snapshot: WorldSnapshotMessage): World {
    const world = snapshotToWorld(snapshot);
    this.map = snapshot.map;
    this.version = snapshot.version;
    this.world = world;
    return world;
  }

  /**
   * Apply a periodic map-less keyframe (issue #119): an **authoritative
   * wholesale replacement** of the entity set that carries the retained map
   * forward, discarding any stale incremental state - the recovery mechanism
   * the incremental path leans on. Returns `undefined` (a safe no-op) when no
   * map-bearing baseline has arrived yet.
   */
  applyKeyframe(keyframe: WorldKeyframeMessage): World | undefined {
    if (this.map === undefined) return undefined;
    return this.adoptSnapshot({
      kind: 'Snapshot',
      version: this.version,
      map: this.map,
      rngState: keyframe.rngState,
      idCounter: keyframe.idCounter,
      entities: keyframe.entities,
      commandQueue: keyframe.commandQueue,
    });
  }

  /**
   * Apply an ordinary field-level delta **incrementally**: mutate only the
   * added/removed/cleared/changed state the delta names, retaining object
   * identity for everything else. Returns the (same) replica world, or
   * `undefined` when there is no baseline yet or the frame was malformed - in
   * the malformed case the (possibly partially-applied) replica world is
   * discarded so later deltas can't compound it; the shell may keep showing
   * that stale-partial world for up to one keyframe interval until the next
   * keyframe/snapshot reseeds (see the module doc).
   */
  applyDelta(delta: WorldDeltaMessage): World | undefined {
    const world = this.world;
    if (world === undefined) return undefined;
    try {
      // JSON decode leaves `removed` ids as they were emitted, but the ids that
      // came from Record keys are strings; the World map is keyed by numeric
      // EntityId, so normalize before the Map lookup.
      for (const id of delta.removed) world.remove(Number(id) as EntityId);
      for (const key of Object.keys(delta.added)) {
        // Record keys are strings after JSON/codec decode; the World map is
        // keyed by numeric EntityId, so rebuild from the snapshot's own id.
        world.add(this.rebuildEntity(delta.added[key as unknown as EntityId]!));
      }
      for (const key of Object.keys(delta.changed)) {
        const id = Number(key) as EntityId;
        const entity = world.get(id);
        // Defensive (the old applyDelta's posture): a field-delta for an entity
        // the replica lacks can only happen on a corrupt/reordered stream - skip
        // it; the periodic keyframe resync is the recovery path.
        if (entity === undefined) continue;
        this.applyFieldDelta(entity, delta.changed[key as unknown as EntityId]!);
      }
      // The tick-volatile scalars + command queue travel in full every delta.
      world.rng.setState(delta.rngState);
      world.idCounter = delta.idCounter;
      world.replacePendingCommands(delta.commandQueue);
      return world;
    } catch {
      // A malformed frame threw part-way: the replica may be partially mutated,
      // so discard it (further deltas become no-ops) and let the next
      // authoritative keyframe/snapshot reseed - never a wedge. The shell's
      // last-emitted world may still be this partial one on screen until that
      // resync lands (<= 1 keyframe interval; see the module doc).
      this.world = undefined;
      return undefined;
    }
  }

  /** Rebuild a freshly `added` entity from its full wire snapshot. */
  private rebuildEntity(snap: EntitySnapshot): Entity {
    const entity: Entity = { id: Number(snap.id) as EntityId };
    const source = snap as unknown as Record<string, unknown>;
    for (const key of Object.keys(source)) {
      if (key === 'id') continue;
      this.applyComponent(entity, key, source[key]);
    }
    return entity;
  }

  /** Merge one entity's field delta: rebuild `set` components, delete `cleared`. */
  private applyFieldDelta(entity: Entity, delta: EntityFieldDelta): void {
    if (delta.set) {
      const set = delta.set as Record<string, unknown>;
      for (const key of Object.keys(set)) this.applyComponent(entity, key, set[key]);
    }
    if (delta.cleared) {
      for (const key of delta.cleared) {
        delete (entity as unknown as Record<string, unknown>)[key];
      }
    }
  }

  /**
   * Apply one component value through its descriptor. A key with no **own**
   * descriptor is dropped, exactly as full reconstruction drops it - so the two
   * models stay equivalent, and a newly *declared* component (one added to the
   * ADR-0260 table) flows through with no client branch at all. The own-property
   * guard also keeps a crafted key like `__proto__` a clean drop (a prototype
   * hit would yield a descriptor-shaped object with no callable `rebuild` and
   * needlessly discard the replica).
   *
   * A falsy value is skipped (the persistence `rebuildEntity`'s `if (!value)`
   * guard). One deliberate asymmetry: on a `changed.set` entry that skip
   * *retains* any existing component, whereas the old merged-baseline model
   * would have carried the falsy value into `rebuildEntity` and dropped it. A
   * well-formed stream never produces a falsy `set` value (`diffEntity` only
   * emits defined component values, all truthy by construction), so this is
   * unreachable in practice - a genuine component removal always arrives as
   * `cleared`, which both models delete identically.
   */
  private applyComponent(entity: Entity, key: string, value: unknown): void {
    if (!value) return;
    if (!Object.hasOwn(this.descriptors, key)) return;
    const descriptor = this.descriptors[key];
    if (descriptor === undefined) return;
    (entity as unknown as Record<string, unknown>)[key] = descriptor.rebuild(value);
  }
}
